import { useState } from "react";
import { editKeyLoanTimes } from "../fetch/KeyRequest";
import { errorMessage } from "../function/errorMessage";
import { toLocalInputValue } from "../function/toJSTDateString";
import styles from "../styles/modules/StorageLocationPopup.module.css";
import keyStyles from "../styles/modules/KeyPage.module.css";
import { toast } from "../toast/toast";
import { KeyLoanDisp } from "../types";

interface KeyLoanEditPopupProps {
  loan: KeyLoanDisp;
  onClose: () => void;
  onSaved: () => void;
}

// 貸出・返却時間の手修正。修正内容は履歴として記録される。
function KeyLoanEditPopup({ loan, onClose, onSaved }: KeyLoanEditPopupProps) {
  // 入力欄（datetime-local）は分単位のため、開いたときの値をそのまま保存すると
  // 触っていない項目でも「秒」が切り捨てられて修正扱いになる。
  // 変更されていない項目は元の値をそのまま送る。
  const initialBorrowedAt = toLocalInputValue(loan.borrowed_at);
  const initialReturnedAt = loan.returned_at
    ? toLocalInputValue(loan.returned_at)
    : "";

  const [borrowedAt, setBorrowedAt] = useState<string>(initialBorrowedAt);
  const [returnedAt, setReturnedAt] = useState<string>(initialReturnedAt);
  const [isSaving, setIsSaving] = useState<boolean>(false);

  async function handleSave() {
    if (!borrowedAt) {
      toast.error("貸出時間を入力してください。");
      return;
    }
    if (returnedAt && new Date(returnedAt) < new Date(borrowedAt)) {
      toast.error("返却時間は貸出時間より後にしてください。");
      return;
    }

    setIsSaving(true);
    try {
      const nextBorrowedAt =
        borrowedAt === initialBorrowedAt
          ? loan.borrowed_at
          : new Date(borrowedAt).toISOString();
      const nextReturnedAt =
        returnedAt === initialReturnedAt
          ? loan.returned_at
          : returnedAt
          ? new Date(returnedAt).toISOString()
          : null;

      await editKeyLoanTimes(loan.id, nextBorrowedAt, nextReturnedAt);
      toast.success("貸出記録を修正しました。");
      onSaved();
    } catch (error) {
      errorMessage("貸出記録の修正に失敗しました。", error);
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <div className={styles["overlay"]} onClick={onClose}>
      <div className={styles["panel"]} onClick={(e) => e.stopPropagation()}>
        <h3 className={styles["title"]}>貸出記録の修正</h3>

        <div className={keyStyles["edit-form"]}>
          <label>
            試薬庫・使用者
            <input
              type="text"
              value={`${loan.storage_location_name ?? "-"} ／ ${
                loan.user_name ?? "-"
              }`}
              readOnly
            />
          </label>
          <label>
            貸出時間
            <input
              type="datetime-local"
              value={borrowedAt}
              onChange={(e) => setBorrowedAt(e.target.value)}
            />
          </label>
          <label>
            返却時間
            <input
              type="datetime-local"
              value={returnedAt}
              onChange={(e) => setReturnedAt(e.target.value)}
            />
          </label>
          <p className={keyStyles["edit-note"]}>
            ※ 修正した内容（修正者・修正日時・修正前後の時間）は記録に残ります。
            <br />※ 他の人の記録は研究室管理者のみ修正できます。
          </p>
        </div>

        <div className={keyStyles["edit-actions"]}>
          <button
            type="button"
            className={keyStyles["save"]}
            onClick={handleSave}
            disabled={isSaving}
          >
            保存
          </button>
          <button type="button" className={keyStyles["cancel"]} onClick={onClose}>
            キャンセル
          </button>
        </div>
      </div>
    </div>
  );
}

export default KeyLoanEditPopup;
